import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { t } from "i18next";
import {
  BaseQueryApi,
  QueryReturnValue,
} from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import toast from "react-hot-toast/headless";
import { AdminState } from "src/apps/admin/stores/AdminStore";
import { msalInstance } from "../../../utils/authentication";
import { setAccessToken, signOut } from "../../../stores/AuthSlice";
import { GetErrorMessage, isFetchArgs } from "../../../utils/apiUtils";

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_ADMIN_INVOICES_API_URL}`,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { accessToken },
    } = getState() as AdminState;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const isPlainTextResponse = (
  queryResult: QueryReturnValue<
        unknown,
        FetchBaseQueryError,
        FetchBaseQueryMeta
    >
) =>
  queryResult.meta?.response?.headers
    .get("content-type")
    ?.includes("text/plain");

const refreshTokenQuery = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  // fetchBaseQuery requires {} as the param
  extraOptions: {}
) => {
  return async () => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const authResult = await msalInstance.acquireTokenSilent({
        scopes: [
          "openid",
          "offline_access",
          import.meta.env.VITE_AZURE_B2C_APP_ID,
        ],
      });

      if (authResult.accessToken) {
        api.dispatch(setAccessToken(authResult.accessToken));
        result = await baseQuery(args, api, extraOptions);
      } else {
        msalInstance.logoutRedirect().then(() => api.dispatch(signOut()));
      }
    }

    return result;
  };
};

const posInvoiceBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const query = refreshTokenQuery(args, api, extraOptions);

  // Enhanced error parsing wrapper
  const attachErrorData = async (
    result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
  ): Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>> => {
    const response = result.meta?.response;
    if (result.error && !result.error.data && response) {
      try {
        const cloned = response.clone();
        (result.error as FetchBaseQueryError).data = await cloned.json();
      } catch {
        // Ignore parse errors
      }
    }
    return result;
  };

  if (!isFetchArgs(args) || args.method === "GET") {
    let result = await query();
    result = await attachErrorData(result);

    if (result.error) {
      toast.error(GetErrorMessage(result.error, isPlainTextResponse(result)));
    }

    return result;
  } else {
    const promise = async () => {
      let result = await query();
      result = await attachErrorData(result);

      const customErrorMessage =
        typeof args === "object" && "customErrorMessage" in args
          ? args.customErrorMessage
          : null;

      if (result.error) {
        // When API call results in error, attach backend JSON (Message, etc.)
        try {
          const response =
            result.meta?.response ??
            (result.error as unknown as { response?: Response }).response;

          if (response) {
            const cloned = response.clone();
            (result.error as FetchBaseQueryError).data = await cloned.json();
          }
        } catch {
          // ignore parsing issues
        }

        // Trigger promise's error response (line 111)
        const backendData = (result.error as FetchBaseQueryError).data as
          | { Message?: string }
          | undefined;

        throw (
          backendData?.Message ||
          customErrorMessage ||
          GetErrorMessage(result.error, isPlainTextResponse(result))
        );
      }

      return result;
    };

    return toast.promise(promise(), {
      loading:
                args.method === "DELETE" ? `${t`Deleting`}...` : `${t`Saving`}...`,
      success: `${t`Success`}!`,
      error: (e) => e,  // this will show the server side message in a toast
    });
  }
};

export const AdminInvoiceApi = createApi({
  reducerPath: "adminInvoiceApi",
  baseQuery: posInvoiceBaseQuery,
  endpoints: () => ({}),
  tagTypes: ["Kds", "Printer", "RaspberryDevice", "CardReader"],
});
